// =============================================================================
// Comissão de professores — produtos
//
// Cada produto reúne as variações dele na Guru (normal, recorrência, split), o
// grupo dele na TMB, o parceiro e o percentual. Como no sistema antigo, a busca
// na Guru pré-marca as variações que colapsam no mesmo nome de curso.
//
// Diferenças do Fly, de propósito:
//   - produto não se exclui: desativa. Ele é referência das apurações fechadas
//     e do Histórico, e apagar levava junto o que já foi pago;
//   - mudar o percentual não muda mês fechado: cada apuração guarda o
//     percentual do momento em que fechou;
//   - a regra é do produto (058). A do Fly, com adiantamento, é a padrão.
// =============================================================================

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, buscarGruposTmb, buscarProdutosComissao, buscarProdutosGuru, FalhaApi, type ProdutoComissao, type RegraComissao } from "../lib/api.ts";
import { Painel, Pilula, Vazio } from "../componentes/ui.tsx";
import { Modal } from "../componentes/Modal.tsx";
import { Icone } from "../componentes/Icone.tsx";

export const REGRA: Record<RegraComissao, { rotulo: string; explica: string }> = {
  adiantamento: {
    rotulo: "Com adiantamento",
    explica: "Pago por volta do dia 10: o Pix e a TMB do mês anterior, mais o cartão e o boleto do mês inteiro — o que já caiu e o que ainda vai cair. O que mudar depois de pago é acertado no repasse seguinte. É a regra do sistema antigo.",
  },
  recebido_no_mes: {
    rotulo: "Só o recebido no mês",
    explica: "Tudo o que caiu na conta no mês, nos quatro canais, pago depois de o mês fechar. O que ainda não caiu entra no mês em que cair.",
  },
};

const pct = (pb: number) => `${(pb / 100).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`;

/** "20" ou "12,5" → pontos-base, por texto: percentual não passa por float. */
export function paraPontosBase(v: string): number | null {
  const m = /^(\d{1,3})(?:[.,](\d{1,2}))?$/.exec(v.trim());
  if (!m) return null;
  const pb = Number(m[1]) * 100 + Number((m[2] ?? "").padEnd(2, "0"));
  return pb >= 1 && pb <= 10_000 ? pb : null;
}

export function ComissaoProdutos() {
  const produtos = useQuery({ queryKey: ["comissao", "produtos"], queryFn: buscarProdutosComissao });
  const [editando, setEditando] = useState<ProdutoComissao | "novo" | null>(null);

  return (
    <div className="pagina space-y-5 pt-6 pb-20">
      {/* O nome e a descrição da tela já estão no cabeçalho do Nexa. */}
      <div className="flex justify-end">
        <button onClick={() => setEditando("novo")}
                className="inline-flex items-center gap-2 rounded-full bg-laranja px-5 py-2.5 text-[13px] font-bold text-azul hover:brightness-95">
          <Icone nome="mais" tamanho={16} /> Novo produto
        </button>
      </div>

      <Painel className="!px-0 !py-0">
        {produtos.isLoading ? (
          <p className="px-6 py-5 text-tinta-fraca">Carregando…</p>
        ) : produtos.error ? (
          <p className="px-6 py-5 text-risco-tinta">{produtos.error instanceof FalhaApi ? produtos.error.message : "Não foi possível carregar."}</p>
        ) : !produtos.data?.produtos.length ? (
          <div className="px-6 py-5"><Vazio titulo="Nenhum produto cadastrado" dica="Comece pelo Novo produto: busque o nome do curso na Guru e marque as variações." /></div>
        ) : (
          <div className="thin-scroll overflow-x-auto">
            <table className="w-full min-w-[860px] text-[13px]">
              <thead className="bg-papel text-left text-[11px] tracking-wide text-tinta-fraca uppercase">
                <tr>
                  <th className="px-6 py-3">Produto</th><th>Parceiro</th><th className="text-center">Comissão</th>
                  <th className="text-center">Variações na Guru</th><th>Grupo na TMB</th><th>Regra</th><th>Último mês fechado</th><th /><th />
                </tr>
              </thead>
              <tbody>
                {produtos.data.produtos.map((p) => (
                  <tr key={p.id} className="border-t border-linha">
                    <td className="px-6 py-3 font-medium text-tinta">{p.nome}</td>
                    <td className="text-tinta-media">{p.parceiro ?? "—"}</td>
                    <td className="num text-center font-semibold text-laranja-tinta">{pct(p.percentual_pb)}</td>
                    <td className="num text-center">{p.guru_produtos.length}</td>
                    <td className="text-tinta-media">{p.grupo_tmb ?? "—"}</td>
                    <td className="text-tinta-media">{REGRA[p.regra].rotulo}</td>
                    <td className="num text-tinta-media">{p.ultimo_mes_fechado?.slice(0, 7) ?? "—"}</td>
                    <td>{p.ativo ? <Pilula tom="ok">Ativo</Pilula> : <Pilula tom="neutro">Inativo</Pilula>}</td>
                    <td className="px-6 text-right">
                      <button onClick={() => setEditando(p)} className="text-[13px] font-medium text-azul-medio hover:underline">Editar</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Painel>

      {editando && <FormularioProduto produto={editando === "novo" ? null : editando} onFechar={() => setEditando(null)} />}
    </div>
  );
}

function FormularioProduto({ produto, onFechar }: { produto: ProdutoComissao | null; onFechar: () => void }) {
  const qc = useQueryClient();
  const [nome, setNome] = useState(produto?.nome ?? "");
  const [parceiro, setParceiro] = useState(produto?.parceiro ?? "");
  const [percentual, setPercentual] = useState(produto ? String(produto.percentual_pb / 100).replace(".", ",") : "");
  const [grupo, setGrupo] = useState(produto?.grupo_tmb ?? "");
  const [ativo, setAtivo] = useState(produto?.ativo ?? true);
  const [regra, setRegra] = useState<RegraComissao>(produto?.regra ?? "adiantamento");
  const [variacoes, setVariacoes] = useState<Map<string, string>>(
    new Map((produto?.guru_produtos ?? []).map((id) => [id, ""])));
  const [termo, setTermo] = useState("");
  const [buscado, setBuscado] = useState("");

  const grupos = useQuery({ queryKey: ["comissao", "grupos-tmb"], queryFn: buscarGruposTmb, staleTime: 10 * 60_000 });
  const busca = useQuery({
    queryKey: ["comissao", "guru", buscado],
    queryFn: () => buscarProdutosGuru(buscado),
    enabled: buscado.length >= 3,
  });

  const buscar = async () => {
    setBuscado(termo.trim());
  };
  // Ao chegar o resultado, pré-marca as variações sugeridas (só em produto novo)
  // e dá nome às que já estavam marcadas.
  const resultado = busca.data;
  useEffect(() => {
    if (!resultado) return;
    setVariacoes((atual) => {
      const m = new Map(atual);
      for (const p of resultado.produtos) if ((p.sugerido && !produto) || m.has(p.id)) m.set(p.id, p.nome);
      return m;
    });
    if (!produto) setNome((n) => n || resultado.sugestao);
  }, [resultado, produto]);

  const pb = paraPontosBase(percentual);
  const salvar = useMutation({
    mutationFn: () => api.post("/api/comissao/produtos", {
      id: produto?.id, nome: nome.trim(), parceiro: parceiro.trim() || null, percentual_pb: pb,
      guru_produtos: [...variacoes.keys()], grupo_tmb: grupo || null, ativo, regra,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["comissao"] }); onFechar(); },
  });
  const alternar = (id: string, nomeVar: string) => {
    const m = new Map(variacoes);
    if (m.has(id)) m.delete(id); else m.set(id, nomeVar);
    setVariacoes(m);
  };
  const campo = "mt-1 w-full rounded-lg border border-linha bg-superficie px-3 py-2 text-[13px] text-tinta";

  return (
    <Modal titulo={produto ? "Editar produto" : "Novo produto"} onFechar={onFechar} largura="max-w-2xl">
      <div className="space-y-4">
        <div>
          <span className="text-[12px] font-semibold text-tinta-fraca">Variações na Guru</span>
          <div className="mt-1 flex gap-2">
            <input value={termo} onChange={(e) => setTermo(e.target.value)} onKeyDown={(e) => e.key === "Enter" && buscar()}
                   placeholder="Parte do nome do curso, ex.: Departamento Pessoal" className={`${campo} mt-0`} />
            <button onClick={buscar} disabled={termo.trim().length < 3}
                    className="shrink-0 rounded-full border border-linha px-4 text-[13px] font-medium hover:bg-papel disabled:opacity-50">
              Buscar
            </button>
          </div>
          {busca.isFetching && <p className="mt-2 text-[12px] text-tinta-fraca">Buscando na Guru…</p>}
          {busca.error && <p className="mt-2 text-[12px] text-risco-tinta">{busca.error instanceof FalhaApi ? busca.error.message : "Falha na busca."}</p>}
          <ul className="thin-scroll mt-2 max-h-48 space-y-1 overflow-auto">
            {[...new Map([...(busca.data?.produtos ?? []).map((p) => [p.id, p.nome] as const), ...variacoes]).entries()].map(([id, n]) => (
              <li key={id}>
                <label className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1 text-[13px] hover:bg-papel">
                  <input type="checkbox" checked={variacoes.has(id)} onChange={() => alternar(id, n)} />
                  <span className="text-tinta">{n || <span className="num text-tinta-fraca">{id}</span>}</span>
                </label>
              </li>
            ))}
          </ul>
          <p className="mt-1 text-[12px] text-tinta-fraca">{variacoes.size} variação(ões) marcada(s). Produto novo tem as vendas carregadas desde 2025 na madrugada seguinte.</p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block text-[12px] font-semibold text-tinta-fraca">Nome no painel
            <input value={nome} onChange={(e) => setNome(e.target.value)} className={campo} />
          </label>
          <label className="block text-[12px] font-semibold text-tinta-fraca">Parceiro
            <input value={parceiro} onChange={(e) => setParceiro(e.target.value)} className={campo} />
          </label>
          <label className="block text-[12px] font-semibold text-tinta-fraca">Comissão (%)
            <input value={percentual} onChange={(e) => setPercentual(e.target.value)} inputMode="decimal" placeholder="20" className={`${campo} num`} />
            {percentual && pb == null && <span className="mt-1 block font-normal text-risco-tinta">Use um número entre 0,01 e 100, com até duas casas.</span>}
          </label>
          <label className="block text-[12px] font-semibold text-tinta-fraca">Grupo na TMB
            <select value={grupo} onChange={(e) => setGrupo(e.target.value)} className={campo}>
              <option value="">Não vende pela TMB</option>
              {grupo && !grupos.data?.grupos.includes(grupo) && <option value={grupo}>{grupo}</option>}
              {(grupos.data?.grupos ?? []).map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </label>
        </div>
        <label className="block text-[12px] font-semibold text-tinta-fraca">Regra de pagamento
          <select value={regra} onChange={(e) => setRegra(e.target.value as RegraComissao)} className={campo}>
            {(Object.keys(REGRA) as RegraComissao[]).map((r) => <option key={r} value={r}>{REGRA[r].rotulo}</option>)}
          </select>
          <span className="mt-1 block font-normal">{REGRA[regra].explica}</span>
          {produto && regra !== produto.regra && (
            <span className="mt-1 block font-normal text-atencao-tinta">
              Vale para os próximos fechamentos. Confira o mês da troca: as janelas das duas regras não se encaixam —
              o que já foi pago não entra de novo, e o que ficar de fora entra por ajuste manual.
            </span>
          )}
        </label>
        {produto && (
          <label className="flex items-center gap-2 text-[13px] text-tinta">
            <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />
            Ativo — desmarcado, o produto sai da apuração e da carga de vendas, e o que já foi fechado continua guardado.
          </label>
        )}
        {salvar.error && <p className="rounded-lg bg-risco-bg px-4 py-3 text-[13px] text-risco-tinta">{salvar.error instanceof FalhaApi ? salvar.error.message : "Não foi possível salvar."}</p>}
        <div className="flex justify-end gap-2">
          <button onClick={onFechar} className="rounded-full border border-linha px-4 py-2 text-[13px]">Cancelar</button>
          <button onClick={() => salvar.mutate()} disabled={!nome.trim() || pb == null || !variacoes.size || salvar.isPending}
                  className="rounded-full bg-laranja px-5 py-2 text-[13px] font-bold text-azul disabled:opacity-50">
            {salvar.isPending ? "Salvando…" : "Salvar"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
